import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, Search } from "lucide-react";
import { type DropdownItem, useToast } from "@/components/ui";
import {
  callingAgentsService,
  type VapiVoice,
} from "@/services/api/callingAgents";

const PROVIDER_LABELS: Record<string, string> = { "11labs": "ElevenLabs" };

export const capitalize = (value: string) =>
  value
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const providerLabel = (p: string) => PROVIDER_LABELS[p] ?? capitalize(p);

interface Options {
  provider: string;
  language: string;
  voiceId: string;
  onSelect: (voiceId: string) => void;
}

// Voices from the Vapi voices API for the selected provider/language, as dropdown
// items with gender/accent tags and an mp3 preview button.
export const useVoiceOptions = ({
  provider,
  language,
  voiceId,
  onSelect,
}: Options) => {
  const { error: toastError } = useToast();
  const [voices, setVoices] = useState<VapiVoice[]>([]);
  // Every voice loaded so far, so the saved voice keeps its name after a search narrows the list
  const [known, setKnown] = useState<VapiVoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  // Wait for typing to pause before hitting the API
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setVoices([]);
    if (!provider) return;
    let cancelled = false;
    setLoading(true);
    callingAgentsService
      // ponytail: first 100 matches only, add paging if a provider exceeds that
      .vapiVoices({
        provider,
        language: language || undefined,
        search: query || undefined,
        limit: 100,
        page: 1,
      })
      .then((res: any) => {
        if (cancelled) return;
        const list: VapiVoice[] = res?.attributes ?? [];
        setVoices(list);
        setKnown((prev) => [
          ...prev,
          ...list.filter((v) => !prev.some((p) => p.providerId === v.providerId)),
        ]);
      })
      .catch(() => {
        if (!cancelled) toastError("Failed to load voices");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [provider, language, query]);

  // Voice sample preview — plays the provider's hosted mp3
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const stopPreview = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingId(null);
  };

  const togglePreview = (e: React.MouseEvent, v: VapiVoice) => {
    e.stopPropagation(); // don't select the voice / close the menu
    const wasActive = playingId === v.id;
    stopPreview();
    if (wasActive || !v.previewUrl) return;
    const audio = new Audio(v.previewUrl);
    audio.onended = () => {
      if (audioRef.current === audio) stopPreview();
    };
    audioRef.current = audio;
    setPlayingId(v.id);
    audio.play().catch(() => {
      stopPreview();
      toastError("Unable to play voice sample");
    });
  };

  useEffect(() => () => audioRef.current?.pause(), []);

  const items: DropdownItem[] = voices.map((v) => ({
    label: `${v.name} (${providerLabel(v.provider)})`,
    suffix: (
      <span className="flex items-center gap-3" title={v.description}>
        {/* Fixed-width column so gender/accent line up across rows */}
        <span className="flex w-40 gap-2">
          {[v.gender, v.accent].filter(Boolean).map((tag) => (
            <span
              key={tag}
              className="min-w-[4rem] rounded-full bg-gray-100 px-2 py-0.5 text-center text-xs text-gray-600"
            >
              {capitalize(tag as string)}
            </span>
          ))}
        </span>
        {v.previewUrl ? (
          <span
            role="button"
            aria-label={`Play ${v.name} sample`}
            onClick={(e) => togglePreview(e, v)}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-button text-white hover:opacity-80"
          >
            {playingId === v.id ? <Pause size={10} /> : <Play size={10} />}
          </span>
        ) : (
          <span className="w-6" />
        )}
      </span>
    ),
    selected: v.providerId === voiceId,
    onClick: () => {
      stopPreview();
      onSelect(v.providerId);
    },
  }));

  const found = known.find((v) => v.providerId === voiceId);
  // ponytail: saved voice only gets a name if it's been loaded at some point; raw id never shown. add a single-voice lookup endpoint if this is not enough
  const selectedLabel = !voiceId
    ? ""
    : found
      ? `${found.name} (${providerLabel(found.provider)})`
      : loading
        ? "Loading voice..."
        : "Saved voice";

  const placeholder = !provider
    ? "Select a voice provider first"
    : loading
      ? "Loading voices..."
      : "Select";

  const header = (
    <div className="relative">
      <Search
        size={14}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
      />
      <input
        type="text"
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search voices"
        aria-label="Search voices"
        className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm outline-none focus:border-primary-button dark:border-gray-600 dark:bg-gray-700"
      />
    </div>
  );

  return { items, selectedLabel, placeholder, header };
};
